import { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, HStack, StackItem } from "@astryxdesign/core/Stack";
import { Text, Heading } from "@astryxdesign/core/Text";
import { Card } from "@astryxdesign/core/Card";
import { Badge } from "@astryxdesign/core/Badge";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Banner } from "@astryxdesign/core/Banner";
import { Selector } from "@astryxdesign/core/Selector";
import { TextInput } from "@astryxdesign/core/TextInput";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { DateInput } from "@astryxdesign/core/DateInput";
import { FileInput } from "@astryxdesign/core/FileInput";
import { Icon } from "@astryxdesign/core/Icon";
import { Tooltip } from "@astryxdesign/core/Tooltip";
import { Spinner } from "@astryxdesign/core/Spinner";
import { Pagination } from "@astryxdesign/core/Pagination";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { TabList, Tab } from "@astryxdesign/core/TabList";
import { Layout, LayoutContent, LayoutFooter } from "@astryxdesign/core/Layout";
import {
  Table,
  proportional,
  pixel,
  useTableSortable,
} from "@astryxdesign/core/Table";
import { useAuth } from "../../contexts/AuthContext";
import { companyService } from "../../services/companyService";
import { downloadsAPI } from "../../services/api";
import {
  isCompanyCertificateLoginBlocked,
  isCompanyDownloadFailure,
  isCompanyLoginFailure,
  isPartialJobStatus,
  resolveCompanyDownloadError,
  resolveLoginStatusError,
} from "../../utils/downloadStatusUtils";
import CSVImport from "../CSVImport/CSVImport";
import DownloadLogsModal from "../DownloadLogsModal/DownloadLogsModal";
import AstryxTheme from "../../theme/AstryxTheme";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "active", label: "Ativo" },
  { value: "inactive", label: "Inativo" },
];

const LOGIN_FILTER_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "ok", label: "OK" },
  { value: "error", label: "Erro de Login" },
  { value: "never", label: "Nunca" },
];

const CERT_FILTER_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "with", label: "Com certificado cadastrado" },
  { value: "without", label: "Sem certificado cadastrado" },
];

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Diário" },
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensal" },
];

const PAGE_SIZE_OPTIONS = [
  { value: "25", label: "25 por página" },
  { value: "50", label: "50 por página" },
  { value: "100", label: "100 por página" },
];

const formatDate = (dateString) => {
  if (!dateString) return "Nunca";
  return new Date(dateString).toLocaleDateString("pt-BR");
};

const formatDateTime = (dateString) => {
  if (!dateString) return "Não informado";
  return new Date(dateString).toLocaleString("pt-BR");
};

export default function CompaniesTable() {
  const { user: currentUser } = useAuth();

  // State management
  const [companies, setCompanies] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Estados para busca server-side
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchingInBackground, setIsSearchingInBackground] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [selectedCompanyForLogs, setSelectedCompanyForLogs] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCSVImportOpen, setIsCSVImportOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [inactivationLogs, setInactivationLogs] = useState([]);
  const [loadingInactivationLogs, setLoadingInactivationLogs] = useState(false);
  const [inactivationLogsError, setInactivationLogsError] = useState("");
  const [lastDownloadJob, setLastDownloadJob] = useState(null);
  const [loadingLastDownloadJob, setLoadingLastDownloadJob] = useState(false);
  const [lastDownloadJobError, setLastDownloadJobError] = useState("");
  const [search, setSearch] = useState("");
  const [isPeriodDownloadModalOpen, setIsPeriodDownloadModalOpen] =
    useState(false);
  const [periodDownloadLoading, setPeriodDownloadLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Filtering and sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "code",
    direction: "asc",
  });
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'active', 'inactive'
  const [loginStatusFilter, setLoginStatusFilter] = useState("all"); // 'all', 'ok', 'error', 'never'
  const [certificateFilter, setCertificateFilter] = useState("all"); // 'all', 'with', 'without'

  // Form data for modals (simplified to match database schema)
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    cnpj: "",
    portal_login: "",
    portal_password: "",
    auto_download_enabled: true,
    download_frequency: "daily",
    nao_captura_nota: false,
  });
  const [certPfxFile, setCertPfxFile] = useState(null);
  const [certPfxPassword, setCertPfxPassword] = useState("");
  /** upload de novo PFX só visível ao expandir (+) para poupar espaço na modal */
  const [certUploadExpanded, setCertUploadExpanded] = useState(false);
  const [certRemoving, setCertRemoving] = useState(false);

  // Aba do modal de edição + contatos de e-mail do cliente (avisos de certificado)
  const [editActiveTab, setEditActiveTab] = useState("dados");
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [savingContact, setSavingContact] = useState(false);
  const [contactBusyId, setContactBusyId] = useState(null);

  const getCompanyListFilters = useCallback(
    (opts = {}) => {
      const status =
        "status" in opts
          ? opts.status || undefined
          : statusFilter !== "all"
            ? statusFilter
            : undefined;
      const loginStatus =
        "loginStatus" in opts
          ? opts.loginStatus || undefined
          : loginStatusFilter !== "all"
            ? loginStatusFilter
            : undefined;
      const certificate =
        "certificate" in opts
          ? opts.certificate || undefined
          : certificateFilter !== "all"
            ? certificateFilter
            : undefined;
      const sortBy = opts.sortBy !== undefined ? opts.sortBy : sortConfig.key;
      const sortDirection =
        opts.sortDirection !== undefined
          ? opts.sortDirection
          : sortConfig.direction;
      const search =
        "search" in opts
          ? opts.search || undefined
          : isSearching && searchTerm
            ? searchTerm
            : undefined;

      const f = { sortBy, sortDirection };
      if (status) f.status = status;
      if (loginStatus) f.loginStatus = loginStatus;
      if (certificate) f.certificate = certificate;
      if (search) f.search = search;
      return f;
    },
    [
      statusFilter,
      loginStatusFilter,
      certificateFilter,
      sortConfig.key,
      sortConfig.direction,
      isSearching,
      searchTerm,
    ],
  );

  const loadCompanies = useCallback(
    async (page = 1, limit = 50, searchFilters = {}) => {
      const showFullScreen = initialLoading && companies.length === 0;
      try {
        if (showFullScreen) {
          setInitialLoading(true);
        } else {
          setRefreshing(true);
        }
        setError(null);

        const filters = {
          page,
          limit,
          ...searchFilters,
        };

        Object.keys(filters).forEach((key) => {
          if (filters[key] === undefined) {
            delete filters[key];
          }
        });

        const result = await companyService.getAll(filters);

        if (result.success) {
          setCompanies(result.companies);
          setPagination(
            result.pagination || {
              page: 1,
              limit: 50,
              total: result.companies.length,
              totalPages: 1,
              hasNext: false,
              hasPrev: false,
            },
          );
        } else {
          setError(result.message);
        }
      } catch (error) {
        setError("Erro ao carregar empresas");
        console.error("Error loading companies:", error);
      } finally {
        setInitialLoading(false);
        setRefreshing(false);
      }
    },
    [initialLoading, companies.length],
  );

  useEffect(() => {
    loadCompanies(1, 50, getCompanyListFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ordenação server-side via plugin do Table (asc ↔ desc, sem estado "sem ordenação")
  const sortState = useMemo(
    () => [
      {
        sortKey: sortConfig.key,
        direction:
          sortConfig.direction === "asc" ? "ascending" : "descending",
      },
    ],
    [sortConfig.key, sortConfig.direction],
  );

  const sortPlugin = useTableSortable({
    sort: sortState,
    onSortChange: (next) => {
      const entry = next[0];
      if (!entry) return;
      const key = entry.sortKey;
      const direction = entry.direction === "ascending" ? "asc" : "desc";
      setSortConfig({ key, direction });
      loadCompanies(
        1,
        pagination.limit || 50,
        getCompanyListFilters({ sortBy: key, sortDirection: direction }),
      );
    },
    allowUnsortedState: false,
  });

  // Função de busca executada por Enter/limpar
  const executeSearch = useCallback(
    async (term) => {
      if (term.trim()) {
        // Atualizar termo de busca
        setSearchTerm(term);
        setIsSearching(true);

        // Mostrar indicador de busca em background
        setIsSearchingInBackground(true);

        try {
          // Buscar sem mostrar loading na tabela
          await loadCompanies(
            1,
            pagination.limit || 50,
            getCompanyListFilters({ search: term }),
          );
        } finally {
          setIsSearchingInBackground(false);
        }
      } else {
        // Limpar busca
        setSearchTerm("");
        setIsSearching(false);
        setIsSearchingInBackground(false);

        try {
          await loadCompanies(
            1,
            pagination.limit || 50,
            getCompanyListFilters({ search: "" }),
          );
        } catch (error) {
          console.error("Error clearing search:", error);
        }
      }
    },
    [pagination.limit, loadCompanies, getCompanyListFilters],
  );

  // Digitar só atualiza o valor; buscar é no Enter. Limpar (botão ✕ ou
  // apagar tudo) desfaz uma busca ativa na hora.
  const handleSearchChange = (value) => {
    setSearch(value);
    if (value === "" && isSearching) {
      executeSearch("");
    }
  };

  // Handler para mudança no filtro de status
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);

    loadCompanies(
      1,
      pagination.limit || 50,
      getCompanyListFilters({
        status: value === "all" ? undefined : value,
      }),
    );
  };

  // Handler para mudança no filtro de login status
  const handleLoginStatusFilterChange = (value) => {
    setLoginStatusFilter(value);

    loadCompanies(
      1,
      pagination.limit || 50,
      getCompanyListFilters({
        loginStatus: value === "all" ? undefined : value,
      }),
    );
  };

  const handleCertificateFilterChange = (value) => {
    setCertificateFilter(value);

    loadCompanies(
      1,
      pagination.limit || 50,
      getCompanyListFilters({
        certificate: value === "all" ? undefined : value,
      }),
    );
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadCompanies(newPage, pagination.limit || 50, getCompanyListFilters());
    }
  };

  const handleLimitChange = (newLimit) => {
    const limit = parseInt(newLimit);
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
    loadCompanies(1, limit, getCompanyListFilters());
  };

  const clearAllFilters = () => {
    setSearch("");
    setIsSearching(false);
    setSearchTerm("");
    setStatusFilter("all");
    setLoginStatusFilter("all");
    setCertificateFilter("all");
    loadCompanies(1, pagination.limit || 50, {
      sortBy: sortConfig.key,
      sortDirection: sortConfig.direction,
    });
  };

  const hasActiveFilters =
    isSearching ||
    statusFilter !== "all" ||
    loginStatusFilter !== "all" ||
    certificateFilter !== "all";

  const filteredCompanies = useMemo(() => {
    if (currentUser?.role !== "cliente" || certificateFilter === "all") {
      return companies;
    }
    if (certificateFilter === "with") {
      return companies.filter((c) => c.cert_status !== "none");
    }
    return companies.filter((c) => c.cert_status === "none");
  }, [companies, certificateFilter, currentUser?.role]);

  // Modal handlers
  const openAddCompanyModal = () => {
    setFormData({
      code: "",
      name: "",
      cnpj: "",
      portal_login: "",
      portal_password: "",
      auto_download_enabled: true,
      download_frequency: "daily",
      nao_captura_nota: false,
    });
    setIsAddModalOpen(true);
  };

  const closeAddCompanyModal = () => {
    setIsAddModalOpen(false);
    setFormData({});
  };

  const loadInactivationLogs = useCallback(async (companyId) => {
    try {
      setLoadingInactivationLogs(true);
      setInactivationLogsError("");
      const result = await companyService.getInactivationLogs(companyId);
      if (result.success) {
        setInactivationLogs(result.logs);
      } else {
        setInactivationLogs([]);
        setInactivationLogsError(
          result.message || "Erro ao carregar histórico",
        );
      }
    } catch (error) {
      setInactivationLogs([]);
      setInactivationLogsError("Erro ao carregar histórico");
      console.error("Error loading inactivation logs:", error);
    } finally {
      setLoadingInactivationLogs(false);
    }
  }, []);

  const loadCompanyContacts = useCallback(async (companyId) => {
    setContactsLoading(true);
    setContactsError("");
    const result = await companyService.getContacts(companyId);
    if (result.success) {
      setContacts(result.contacts);
    } else {
      setContacts([]);
      setContactsError(result.message || "Erro ao carregar contatos");
    }
    setContactsLoading(false);
  }, []);

  const openEditCompanyModal = (company) => {
    setEditingCompany(company);
    setCertPfxFile(null);
    setCertPfxPassword("");
    setCertUploadExpanded(false);
    setEditActiveTab("dados");
    setContacts([]);
    setContactsError("");
    setNewContactName("");
    setNewContactEmail("");
    setSavingContact(false);
    setContactBusyId(null);
    loadCompanyContacts(company.id);
    setFormData({
      code: company.code || "",
      name: company.name || "",
      cnpj: company.cnpj || "",
      portal_login: company.portal_login || "",
      portal_password: "", // Don't prefill password
      auto_download_enabled: company.auto_download_enabled !== false,
      download_frequency: company.download_frequency || "daily",
      nao_captura_nota: company.nao_captura_nota === true,
    });
    setInactivationLogs([]);
    setInactivationLogsError("");
    setLastDownloadJob(null);
    setLastDownloadJobError("");
    setLoadingLastDownloadJob(true);
    companyService
      .getLastDownloadJob(company.id)
      .then((result) => {
        if (result.success) {
          setLastDownloadJob(result.job);
          setLastDownloadJobError("");
        } else {
          setLastDownloadJob(null);
          setLastDownloadJobError(
            result.message || "Nenhum processamento encontrado",
          );
        }
      })
      .catch((jobError) => {
        console.error("Error loading last download job:", jobError);
        setLastDownloadJob(null);
        setLastDownloadJobError("Erro ao carregar último processamento");
      })
      .finally(() => {
        setLoadingLastDownloadJob(false);
      });
    if (company.status === "inactive") {
      loadInactivationLogs(company.id);
    }
    setIsEditModalOpen(true);
  };

  const closeEditCompanyModal = () => {
    setIsEditModalOpen(false);
    setEditingCompany(null);
    setIsAuditModalOpen(false);
    setInactivationLogs([]);
    setInactivationLogsError("");
    setLastDownloadJob(null);
    setLoadingLastDownloadJob(false);
    setLastDownloadJobError("");
    setCertPfxFile(null);
    setCertPfxPassword("");
    setCertUploadExpanded(false);
    setCertRemoving(false);
    setEditActiveTab("dados");
    setContacts([]);
    setContactsError("");
    setNewContactName("");
    setNewContactEmail("");
    setSavingContact(false);
    setContactBusyId(null);
    setFormData({});
  };

  const handleAddContact = async () => {
    if (!editingCompany?.id || savingContact) return;
    const email = newContactEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      alert("Informe um e-mail válido.");
      return;
    }
    setSavingContact(true);
    const result = await companyService.addContact(editingCompany.id, {
      name: newContactName.trim() || undefined,
      email,
    });
    setSavingContact(false);
    if (!result.success) {
      alert(result.message || "Erro ao adicionar contato");
      return;
    }
    setNewContactName("");
    setNewContactEmail("");
    await loadCompanyContacts(editingCompany.id);
  };

  const handleToggleContactActive = async (contact) => {
    if (!editingCompany?.id || contactBusyId) return;
    setContactBusyId(contact.id);
    const result = await companyService.updateContact(
      editingCompany.id,
      contact.id,
      { is_active: !contact.is_active },
    );
    setContactBusyId(null);
    if (!result.success || !result.contact) {
      alert(result.message || "Erro ao atualizar contato");
      return;
    }
    setContacts((prev) =>
      prev.map((c) => (c.id === contact.id ? result.contact : c)),
    );
  };

  const handleDeleteContact = async (contact) => {
    if (!editingCompany?.id || contactBusyId) return;
    if (!window.confirm(`Remover o contato ${contact.email}?`)) return;
    setContactBusyId(contact.id);
    const result = await companyService.deleteContact(
      editingCompany.id,
      contact.id,
    );
    setContactBusyId(null);
    if (!result.success) {
      alert(result.message || "Erro ao remover contato");
      return;
    }
    setContacts((prev) => prev.filter((c) => c.id !== contact.id));
  };

  const handleRemoveCertificate = async () => {
    if (!editingCompany?.id) return;
    if (editingCompany.cert_status === "none") {
      alert("Não há certificado para remover.");
      return;
    }
    if (
      !window.confirm(
        "Remover o certificado digital desta empresa? O download com certificado deixará de funcionar até enviar um novo ficheiro.",
      )
    ) {
      return;
    }
    try {
      setCertRemoving(true);
      const result = await companyService.deleteCertificate(editingCompany.id);
      if (!result.success) {
        alert(result.message || "Erro ao remover certificado");
        return;
      }
      if (result.company) {
        setEditingCompany(result.company);
      } else {
        const refreshed = await companyService.getById(editingCompany.id);
        if (refreshed.success && refreshed.company) {
          setEditingCompany(refreshed.company);
        }
      }
      setCertPfxFile(null);
      setCertPfxPassword("");
      setCertUploadExpanded(false);
      await loadCompanies(
        pagination.page,
        pagination.limit,
        getCompanyListFilters(),
      );
      alert(result.message || "Certificado removido.");
    } catch (err) {
      alert("Erro ao remover certificado");
      console.error(err);
    } finally {
      setCertRemoving(false);
    }
  };

  const openAuditModal = async () => {
    if (!editingCompany) return;
    setIsAuditModalOpen(true);
    await loadInactivationLogs(editingCompany.id);
  };

  const closeAuditModal = () => {
    setIsAuditModalOpen(false);
  };

  // Campos controlados dos formulários (inputs do Astryx entregam o valor direto)
  const setFormField = (name) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Substitui o `required` nativo dos inputs antigos (TextInput do Astryx
    // marca aria-required mas não bloqueia o submit sozinho).
    if (!String(formData.name || "").trim() || !String(formData.cnpj || "").trim()) {
      alert("Nome e CNPJ são obrigatórios.");
      return;
    }

    try {
      let result;

      if (isAddModalOpen) {
        // Create new company
        result = await companyService.create(formData);
        if (result.success) {
          await loadCompanies(
            pagination.page,
            pagination.limit,
            getCompanyListFilters(),
          ); // Refresh current page
          closeAddCompanyModal();
          alert("Empresa criada com sucesso!");
        }
      } else if (isEditModalOpen && editingCompany) {
        // Update existing company
        const updateData = { ...formData };
        const passwordToUpdate = updateData.portal_password?.trim();

        // Remove portal_password from updateData (it uses a separate endpoint)
        delete updateData.portal_password;

        if (certPfxFile) {
          if (!String(certPfxPassword).trim()) {
            alert("Informe a palavra-passe do ficheiro PFX");
            return;
          }
          const upCert = await companyService.uploadCertificate(
            editingCompany.id,
            certPfxFile,
            certPfxPassword,
          );
          if (!upCert.success) {
            alert(upCert.message || "Erro ao enviar certificado");
            return;
          }
        }

        // Update company data (without password)
        result = await companyService.update(editingCompany.id, updateData);

        // If password was provided, update it separately
        if (result.success && passwordToUpdate) {
          const passwordResult = await companyService.updatePortalPassword(
            editingCompany.id,
            passwordToUpdate,
          );

          if (!passwordResult.success) {
            // If password update fails, show error but keep company update success
            alert(
              `Empresa atualizada, mas erro ao atualizar senha: ${passwordResult.message}`,
            );
            console.error("Password update error:", passwordResult);
          }
        }

        if (result.success) {
          await loadCompanies(
            pagination.page,
            pagination.limit,
            getCompanyListFilters(),
          ); // Refresh current page
          closeEditCompanyModal();

          // Show appropriate success message
          if (passwordToUpdate) {
            alert("Empresa e senha atualizadas com sucesso!");
          } else {
            alert("Empresa atualizada com sucesso!");
          }
        }
      }

      if (!result.success) {
        alert(`Erro: ${result.message}`);
      }
    } catch (error) {
      alert("Erro ao salvar empresa");
      console.error("Save company error:", error);
    }
  };

  const toggleCompanyStatus = async (companyId) => {
    const company = companies.find((c) => c.id === companyId);
    const action = company?.status === "active" ? "inativar" : "ativar";

    if (window.confirm(`Tem certeza que deseja ${action} esta empresa?`)) {
      try {
        const result = await companyService.toggleStatus(companyId);
        if (result.success) {
          await loadCompanies(
            pagination.page,
            pagination.limit,
            getCompanyListFilters(),
          ); // Refresh current page
          closeEditCompanyModal();
          alert(result.message);
        } else {
          alert(`Erro: ${result.message}`);
        }
      } catch (error) {
        alert("Erro ao alterar status da empresa");
        console.error("Toggle company status error:", error);
      }
    }
  };

  const inactivateCompany = async (companyId) => {
    if (
      window.confirm(
        "Tem certeza que deseja inativar esta empresa? Ela será marcada como inativa mas não será removida do sistema.",
      )
    ) {
      try {
        const result = await companyService.inactivate(companyId);
        if (result.success) {
          await loadCompanies(
            pagination.page,
            pagination.limit,
            getCompanyListFilters(),
          ); // Refresh current page
          closeEditCompanyModal();
          alert(result.message);
        } else {
          alert(`Erro: ${result.message}`);
        }
      } catch (error) {
        alert("Erro ao inativar empresa");
        console.error("Inactivate company error:", error);
      }
    }
  };

  const deleteCompany = async (companyId) => {
    if (
      window.confirm(
        "ATENÇÃO: Tem certeza que deseja DELETAR PERMANENTEMENTE esta empresa?\n\n• Esta ação NÃO PODE ser desfeita\n• Todos os dados da empresa serão removidos\n• Histórico de downloads será perdido\n\nSe você quer apenas desativar, use o botão 'Inativar Empresa'.",
      )
    ) {
      try {
        const result = await companyService.delete(companyId);
        if (result.success) {
          await loadCompanies(
            pagination.page,
            pagination.limit,
            getCompanyListFilters(),
          ); // Refresh current page
          closeEditCompanyModal();
          alert("Empresa deletada permanentemente com sucesso!");
        } else {
          alert(`Erro: ${result.message}`);
        }
      } catch (error) {
        alert("Erro ao deletar empresa");
        console.error("Delete company error:", error);
      }
    }
  };

  // CSV Import handlers
  const openCSVImportModal = () => {
    setIsCSVImportOpen(true);
  };

  const closeCSVImportModal = () => {
    setIsCSVImportOpen(false);
  };

  const handleCSVImportComplete = async (result) => {
    // Refresh companies list after successful import
    await loadCompanies(1, pagination.limit, getCompanyListFilters()); // Go to first page after import

    // Show success message
    const { imported, errors, total } = result.data;
    let message = `Importação concluída!\n\n`;
    message += `Total de linhas: ${total}\n`;
    message += `Empresas criadas: ${imported}\n`;
    message += `Erros: ${errors}`;

    if (errors > 0) {
      message += `\n\nVerifique os detalhes dos erros no modal de importação.`;
    }

    alert(message);
  };

  // Handle viewing company logs
  const viewCompanyLogs = useCallback(async (company) => {
    setSelectedCompanyForLogs(company);
    setIsLogsModalOpen(true);
  }, []);

  // Close logs modal
  const closeLogsModal = useCallback(() => {
    setIsLogsModalOpen(false);
    setSelectedCompanyForLogs(null);
  }, []);

  // Period download handlers
  const openPeriodDownloadModal = () => {
    setIsPeriodDownloadModalOpen(true);
  };

  const closePeriodDownloadModal = () => {
    setIsPeriodDownloadModalOpen(false);
  };

  const handlePeriodDownload = async (startDate, endDate, companyGroup) => {
    try {
      setPeriodDownloadLoading(true);
      const result = await downloadsAPI.startPeriodDownload(
        startDate,
        endDate,
        companyGroup,
      );

      if (result.success) {
        alert(
          `Download iniciado com sucesso!\n\nJob ID: ${result.jobId}\nEmpresas: ${result.companiesCount}`,
        );
        await loadCompanies(
          pagination.page,
          pagination.limit,
          getCompanyListFilters(),
        ); // Refresh current page
        closePeriodDownloadModal();
      } else {
        alert(`Erro ao iniciar download: ${result.message}`);
      }
    } catch (error) {
      console.error("Period download error:", error);
      alert(`Erro ao iniciar download: ${error.message}`);
    } finally {
      setPeriodDownloadLoading(false);
    }
  };

  // Handle export to Excel
  const handleExportExcel = async () => {
    try {
      setExportLoading(true);

      // Build filters object from current state
      const filters = {};
      if (isSearching && searchTerm) {
        filters.search = searchTerm;
      }
      if (statusFilter !== "all") {
        filters.status = statusFilter;
      }
      if (loginStatusFilter !== "all") {
        filters.loginStatus = loginStatusFilter;
      }
      if (certificateFilter !== "all") {
        filters.certificate = certificateFilter;
      }
      if (sortConfig.key) {
        filters.sortBy = sortConfig.key;
      }
      if (sortConfig.direction) {
        filters.sortDirection = sortConfig.direction;
      }

      const result = await companyService.exportToExcel(filters);

      if (result.success) {
        // Success message is optional since download happens automatically
      } else {
        alert(`Erro ao exportar empresas: ${result.message}`);
      }
    } catch (error) {
      console.error("Export error:", error);
      alert(`Erro ao exportar empresas: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const active = status === "active";
    return (
      <Badge
        variant={active ? "success" : "error"}
        label={active ? "Ativo" : "Inativo"}
      />
    );
  };

  const getLoginStatusBadge = (company) => {
    if (isCompanyLoginFailure(company)) {
      const certBlocked = isCompanyCertificateLoginBlocked(company);
      const errorText =
        resolveLoginStatusError(company) ||
        "Falha de login no último processamento";
      return (
        <Tooltip
          content={`Erro: ${errorText}${
            company.last_job_processed_at
              ? ` (${formatDate(company.last_job_processed_at)})`
              : ""
          }`}
        >
          <Badge
            variant="error"
            label={certBlocked ? "Cert. inválido" : "Erro de Login"}
          />
        </Tooltip>
      );
    }

    if (company.last_successful_login) {
      return <Badge variant="success" label="OK" />;
    }

    return <Badge variant="neutral" label="Nunca" />;
  };

  const getCertBadge = (company) => {
    const status = company.cert_status || "none";
    const exp = company.cert_expires_at;
    const days = company.days_until_expiry;
    const d = exp ? new Date(exp).toLocaleDateString("pt-BR") : null;
    let variant = "neutral";
    let text = "Sem cert.";
    let title = "Sem certificado digital no sistema";
    if (status === "valid") {
      variant = "success";
      text = "Válido";
      title = `Certificado válido. Vence em ${d || "—"}`;
    } else if (status === "expiring") {
      variant = "warning";
      text = "A vencer";
      title = `Expira em ${d || "—"}${
        days != null ? ` (em ${days} dia(s))` : ""
      }`;
    } else if (status === "expired") {
      variant = "error";
      text = "Vencido";
      title = `Vencido em ${d || "—"}`;
    }
    return (
      <Tooltip content={title}>
        <Badge variant={variant} label={text} />
      </Tooltip>
    );
  };

  const getDownloadStatusBadge = (company) => {
    const status = company.last_download_job_status;
    const downloaded = Number(company.last_download_downloaded_notes || 0);
    const companyError = resolveCompanyDownloadError(company);

    // Flag "Não captura nota": fora de toda coleta, o status do último job não importa.
    if (company.nao_captura_nota) {
      return (
        <Tooltip content="Empresa marcada como 'Não captura nota' — fora de toda coleta">
          <Badge variant="neutral" label="Não captura" />
        </Tooltip>
      );
    }

    // Empresas com certificado coletam via ADN, que NÃO passa por download_jobs.
    // Se há notas coletadas por ADN, o status real é "Sucesso" — o job de scraping
    // legado (frequentemente zerado por CAPTCHA) não deve mascarar como "Sem notas".
    const adnNotes = Number(company.adn_notes_count || 0);
    if (adnNotes > 0) {
      const syncTxt = company.last_adn_sync_at
        ? ` (última sync ${formatDateTime(company.last_adn_sync_at)})`
        : "";
      return (
        <Tooltip
          content={`${adnNotes} nota(s) coletada(s) via ADN/certificado${syncTxt}`}
        >
          <Badge variant="success" label="Sucesso" />
        </Tooltip>
      );
    }

    if (!status) {
      if (company.last_download_at) {
        const total = Number(company.total_notes_downloaded || 0);
        if (total > 0) {
          return <Badge variant="success" label="Sucesso" />;
        }
        return <Badge variant="warning" label="Sem notas" />;
      }
      return <Badge variant="neutral" label="Nunca" />;
    }

    if (isCompanyDownloadFailure(company)) {
      const certBlocked = isCompanyCertificateLoginBlocked(company);
      return (
        <Tooltip
          content={
            certBlocked
              ? resolveLoginStatusError(company)
              : companyError || "Falha no último processamento"
          }
        >
          <Badge
            variant="error"
            label={certBlocked ? "Cert. inválido" : "Falhou"}
          />
        </Tooltip>
      );
    }

    if (status === "completed" && downloaded > 0) {
      return <Badge variant="success" label="Sucesso" />;
    }

    if (status === "completed") {
      return <Badge variant="warning" label="Sem notas" />;
    }

    // Coleta concluída mas incompleta (paginação truncada, chunk com falha,
    // checkpoint ADN congelado): antes caía em "Desconhecido".
    if (isPartialJobStatus(status)) {
      return (
        <Tooltip content={companyError || "Concluído com erros — parte do período pode não ter sido coletada"}>
          <Badge variant="warning" label="Com erros" />
        </Tooltip>
      );
    }

    if (status === "running") {
      return <Badge variant="info" label="Em execução" />;
    }

    if (status === "pending") {
      return <Badge variant="warning" label="Pendente" />;
    }

    if (status === "cancelled") {
      return <Badge variant="neutral" label="Cancelado" />;
    }

    return <Badge variant="neutral" label="Desconhecido" />;
  };

  const getEmptyStateTitle = () => {
    if (search && search.trim()) return "Nenhuma empresa encontrada para a busca";
    if (statusFilter === "active") return "Nenhuma empresa ativa encontrada";
    if (statusFilter === "inactive")
      return "Nenhuma empresa inativa encontrada";
    if (loginStatusFilter === "ok")
      return "Nenhuma empresa com login OK encontrada";
    if (loginStatusFilter === "error")
      return "Nenhuma empresa com erro de login encontrada";
    if (loginStatusFilter === "never")
      return "Nenhuma empresa que nunca fez login encontrada";
    if (certificateFilter === "with")
      return "Nenhuma empresa com certificado digital cadastrado";
    if (certificateFilter === "without")
      return "Nenhuma empresa sem certificado digital (nestes critérios)";
    return "Nenhuma empresa encontrada";
  };

  const getEmptyStateDescription = () => {
    if (search && search.trim())
      return `Nenhuma empresa corresponde ao termo "${search}". Tente buscar por nome, código ou CNPJ.`;
    if (statusFilter === "active")
      return "Todas as empresas estão inativas no momento.";
    if (statusFilter === "inactive")
      return "Todas as empresas estão ativas no momento.";
    if (loginStatusFilter === "ok")
      return "Nenhuma empresa teve login bem-sucedido.";
    if (loginStatusFilter === "error")
      return "Nenhuma empresa teve erro de login.";
    if (loginStatusFilter === "never")
      return "Todas as empresas já fizeram login pelo menos uma vez.";
    if (certificateFilter === "with")
      return "Nenhuma empresa corresponde ao filtro de certificado cadastrado.";
    if (certificateFilter === "without")
      return "Todas as empresas listadas têm certificado cadastrado, ou utilize outros filtros.";
    return "Ajuste os filtros para encontrar empresas.";
  };

  // Colunas da tabela (data-driven; ordenação delegada ao plugin sortable —
  // sortKey precisa casar com os sortBy aceitos pelo backend)
  const tableColumns = [
    {
      key: "code",
      header: "Código",
      width: pixel(100),
      sortable: true,
      renderCell: (c) => <Text weight="semibold">{c.code || "-"}</Text>,
    },
    {
      key: "name",
      header: "Nome da Empresa",
      width: proportional(3),
      sortable: true,
      renderCell: (c) => <Text size="sm">{c.name}</Text>,
    },
    {
      key: "cnpj",
      header: "CNPJ",
      width: pixel(160),
      sortable: true,
      renderCell: (c) => <Text size="sm">{c.cnpj}</Text>,
    },
    {
      key: "status",
      header: "Status",
      width: pixel(110),
      sortable: true,
      renderCell: (c) => getStatusBadge(c.status),
    },
    {
      key: "loginStatus",
      header: "Login",
      width: pixel(140),
      sortable: true,
      renderCell: (c) => getLoginStatusBadge(c),
    },
    {
      key: "cert",
      header: "Certificado ICP",
      width: pixel(130),
      renderCell: (c) => getCertBadge(c),
    },
    {
      key: "lastDownload",
      header: "Download",
      width: pixel(130),
      sortable: true,
      renderCell: (c) => getDownloadStatusBadge(c),
    },
    {
      key: "processedAt",
      header: "Último Processamento",
      width: pixel(150),
      renderCell: (c) => (
        <Text type="supporting">
          {c.last_job_processed_at || c.last_download_at
            ? formatDate(c.last_job_processed_at || c.last_download_at)
            : "Nunca"}
        </Text>
      ),
    },
    ...(currentUser?.role !== "cliente"
      ? [
          {
            key: "actions",
            header: "Ações",
            width: pixel(130),
            renderCell: (c) => (
              <HStack gap={1}>
                <IconButton
                  label="Editar Empresa"
                  tooltip="Editar Empresa"
                  icon={<FontAwesomeIcon icon="edit" />}
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditCompanyModal(c)}
                />
                <IconButton
                  label="Ver Logs de Download"
                  tooltip="Ver Logs de Download"
                  icon={<FontAwesomeIcon icon="file-alt" />}
                  variant="ghost"
                  size="sm"
                  onClick={() => viewCompanyLogs(c)}
                />
                {currentUser?.role === "admin" && (
                  <IconButton
                    label="Deletar Empresa"
                    tooltip="Deletar Empresa"
                    icon={<FontAwesomeIcon icon="trash" />}
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteCompany(c.id)}
                  />
                )}
              </HStack>
            ),
          },
        ]
      : []),
  ];

  if (initialLoading && companies.length === 0 && !error) {
    return (
      <AstryxTheme>
        <Card padding={6}>
          <Stack gap={2}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} height={48} index={i} />
            ))}
            <Text type="supporting" justify="center">
              Carregando empresas...
            </Text>
          </Stack>
        </Card>
      </AstryxTheme>
    );
  }

  // Error state
  if (error) {
    return (
      <AstryxTheme>
        <Card padding={6}>
          <Stack gap={3} hAlign="center">
            <EmptyState
              title="Erro ao Carregar Empresas"
              description={error}
              icon={<FontAwesomeIcon icon="exclamation-triangle" />}
            />
            <Button
              label="Tentar Novamente"
              variant="primary"
              icon={<FontAwesomeIcon icon="sync" />}
              onClick={() => {
                loadCompanies(
                  pagination.page,
                  pagination.limit,
                  getCompanyListFilters(),
                );
              }}
            />
          </Stack>
        </Card>
      </AstryxTheme>
    );
  }

  return (
    <AstryxTheme>
      <Stack as="section" aria-label="Empresas" gap={4} width="100%">
        {/* Cabeçalho: título + ações */}
        <HStack gap={3} vAlign="center" hAlign="between" wrap="wrap">
          <Stack gap={0.5}>
            <HStack gap={2} vAlign="center">
              <Heading level={2}>
                {currentUser?.role === "cliente"
                  ? "Minhas Empresas"
                  : "Empresas"}
              </Heading>
              {(refreshing || isSearchingInBackground) && (
                <Spinner size="sm" label="Atualizando lista de empresas" />
              )}
            </HStack>
            <Text type="supporting">
              {isSearching
                ? `Mostrando resultados da busca por "${searchTerm}"`
                : hasActiveFilters
                  ? "Mostrando empresas filtradas"
                  : "Gerencie as empresas e suas configurações de download"}
            </Text>
          </Stack>
          {currentUser?.role !== "cliente" && (
            <HStack gap={2} vAlign="center" wrap="wrap">
              {/* Ações restritas a administradores */}
              {currentUser?.role === "admin" && (
                <>
                  <Button
                    label="Download Período"
                    variant="ghost"
                    icon={<FontAwesomeIcon icon="download" />}
                    onClick={openPeriodDownloadModal}
                  />
                  <Button
                    label={exportLoading ? "Exportando..." : "Exportar Excel"}
                    variant="ghost"
                    icon={<FontAwesomeIcon icon="file-alt" />}
                    isLoading={exportLoading}
                    onClick={handleExportExcel}
                  />
                  <Button
                    label="Importar Excel"
                    variant="ghost"
                    icon={<FontAwesomeIcon icon="upload" />}
                    onClick={openCSVImportModal}
                  />
                </>
              )}
              <Button
                label="Nova Empresa"
                variant="primary"
                icon={<FontAwesomeIcon icon="plus" />}
                onClick={openAddCompanyModal}
              />
            </HStack>
          )}
        </HStack>

        {/* Busca e filtros */}
        <HStack gap={2} vAlign="end" wrap="wrap">
          <StackItem size="fill">
            <TextInput
              label="Pesquisar"
              placeholder="Nome, código ou CNPJ (Enter para buscar)"
              value={search}
              onChange={handleSearchChange}
              onEnter={() => executeSearch(search)}
              startIcon={<Icon icon="search" />}
              hasClear
              width="100%"
            />
          </StackItem>
          <Selector
            label="Certificado digital"
            options={CERT_FILTER_OPTIONS}
            value={certificateFilter}
            onChange={handleCertificateFilterChange}
            width={220}
          />
          <Selector
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={handleStatusFilterChange}
            width={130}
          />
          <Selector
            label="Login"
            options={LOGIN_FILTER_OPTIONS}
            value={loginStatusFilter}
            onChange={handleLoginStatusFilterChange}
            width={150}
          />
          {hasActiveFilters && (
            <IconButton
              label="Limpar todos os filtros"
              tooltip="Limpar todos os filtros"
              icon={<FontAwesomeIcon icon="times" />}
              variant="ghost"
              onClick={clearAllFilters}
            />
          )}
        </HStack>

        {/* Tabela / estado vazio */}
        {filteredCompanies.length === 0 ? (
          <Card padding={6}>
            <EmptyState
              title={getEmptyStateTitle()}
              description={getEmptyStateDescription()}
              icon={<FontAwesomeIcon icon="building" />}
            />
          </Card>
        ) : (
          <Table
            data={filteredCompanies}
            columns={tableColumns}
            idKey="id"
            hasHover
            density="balanced"
            plugins={{ sort: sortPlugin }}
          />
        )}

        {/* Paginação */}
        {pagination.totalPages > 1 && (
          <HStack gap={2} vAlign="center" hAlign="between" wrap="wrap">
            <HStack gap={2} vAlign="center">
              <Text type="supporting">
                Mostrando {(pagination.page - 1) * pagination.limit + 1} a{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                de {pagination.total} empresas
              </Text>
              <Selector
                label="Empresas por página"
                isLabelHidden
                options={PAGE_SIZE_OPTIONS}
                value={String(pagination.limit)}
                onChange={handleLimitChange}
                size="sm"
                width={150}
              />
            </HStack>
            <Pagination
              page={pagination.page}
              onChange={handlePageChange}
              totalItems={pagination.total ?? 0}
              pageSize={pagination.limit || 50}
            />
          </HStack>
        )}

        {/* Modal de Adicionar Empresa */}
        {isAddModalOpen && currentUser?.role !== "cliente" && (
          <Dialog
            isOpen
            onOpenChange={(open) => {
              if (!open) closeAddCompanyModal();
            }}
            width={520}
          >
            <form onSubmit={handleSubmit} style={{ display: "contents" }}>
              <Layout
                header={
                  <DialogHeader
                    title="Adicionar Nova Empresa"
                    onOpenChange={(open) => {
                      if (!open) closeAddCompanyModal();
                    }}
                    hasDivider
                  />
                }
                content={
                  <LayoutContent>
                    <Stack gap={3}>
                      <TextInput
                        label="Código da Empresa"
                        isRequired
                        hasAutoFocus
                        placeholder="Ex: 12"
                        value={formData.code}
                        onChange={setFormField("code")}
                      />
                      <TextInput
                        label="Nome da Empresa"
                        isRequired
                        value={formData.name}
                        onChange={setFormField("name")}
                      />
                      <TextInput
                        label="CNPJ"
                        isRequired
                        placeholder="12.345.678/0001-90"
                        value={formData.cnpj}
                        onChange={setFormField("cnpj")}
                      />
                      <TextInput
                        label="Login do Portal Nacional"
                        isOptional
                        placeholder="Login de acesso ao portal"
                        value={formData.portal_login}
                        onChange={setFormField("portal_login")}
                      />
                      <TextInput
                        label="Senha do Portal Nacional"
                        isOptional
                        type="password"
                        htmlName="portal_password"
                        placeholder="Senha de acesso ao portal"
                        value={formData.portal_password}
                        onChange={setFormField("portal_password")}
                      />
                      <HStack gap={3} vAlign="end">
                        <StackItem size="fill">
                          <Selector
                            label="Frequência"
                            options={FREQUENCY_OPTIONS}
                            value={formData.download_frequency}
                            onChange={setFormField("download_frequency")}
                          />
                        </StackItem>
                        <CheckboxInput
                          label="Download Automático"
                          value={formData.auto_download_enabled}
                          onChange={setFormField("auto_download_enabled")}
                        />
                        <CheckboxInput
                          label="Não captura nota"
                          value={formData.nao_captura_nota}
                          onChange={setFormField("nao_captura_nota")}
                        />
                      </HStack>
                    </Stack>
                  </LayoutContent>
                }
                footer={
                  <LayoutFooter hasDivider>
                    <HStack gap={2} hAlign="end">
                      <Button
                        label="Cancelar"
                        variant="ghost"
                        onClick={closeAddCompanyModal}
                      />
                      <Button
                        label="Adicionar Empresa"
                        variant="primary"
                        type="submit"
                        icon={<FontAwesomeIcon icon="plus" />}
                      />
                    </HStack>
                  </LayoutFooter>
                }
              />
            </form>
          </Dialog>
        )}

        {/* Modal de Editar Empresa */}
        {isEditModalOpen &&
          editingCompany &&
          currentUser?.role !== "cliente" && (
            <Dialog
              isOpen
              onOpenChange={(open) => {
                if (!open) closeEditCompanyModal();
              }}
              width={560}
            >
              <form onSubmit={handleSubmit} style={{ display: "contents" }}>
                <Layout
                  header={
                    <DialogHeader
                      title="Editar Empresa"
                      onOpenChange={(open) => {
                        if (!open) closeEditCompanyModal();
                      }}
                      hasDivider
                    />
                  }
                  content={
                    <LayoutContent>
                      <Stack gap={3}>
                        <TabList
                          label="Seções da empresa"
                          value={editActiveTab}
                          onChange={setEditActiveTab}
                          layout="fill"
                        >
                          <Tab
                            value="dados"
                            label="Dados"
                            icon={<FontAwesomeIcon icon="building" />}
                          />
                          <Tab
                            value="contatos"
                            label="Contatos"
                            icon={<FontAwesomeIcon icon="envelope" />}
                          />
                        </TabList>

                        {editActiveTab === "dados" && (
                        <Stack gap={3}>
                        <HStack gap={2} hAlign="end">
                          <Button
                            label="Auditoria"
                            variant="ghost"
                            size="sm"
                            icon={<FontAwesomeIcon icon="info-circle" />}
                            onClick={openAuditModal}
                          />
                        </HStack>

                        {/* Resumo do último processamento */}
                        <Card padding={3}>
                          <Stack gap={1}>
                            <Text weight="semibold">Último processamento</Text>
                            {loadingLastDownloadJob ? (
                              <Text type="supporting">
                                Carregando dados do job...
                              </Text>
                            ) : lastDownloadJobError ? (
                              <Text type="supporting">{lastDownloadJobError}</Text>
                            ) : lastDownloadJob ? (
                              <Stack gap={0.5}>
                                <Text size="sm">
                                  <strong>Status Job:</strong>{" "}
                                  {lastDownloadJob.status === "completed"
                                    ? Number(
                                        lastDownloadJob.total_notes_downloaded || 0,
                                      ) > 0
                                      ? "Sucesso"
                                      : "Sem notas"
                                    : isPartialJobStatus(lastDownloadJob.status)
                                      ? "Concluído com erros"
                                      : lastDownloadJob.status === "failed"
                                      ? "Falhou"
                                      : lastDownloadJob.status === "running"
                                        ? "Em execução"
                                        : lastDownloadJob.status === "pending"
                                          ? "Pendente"
                                          : lastDownloadJob.status === "cancelled"
                                            ? "Cancelado"
                                            : "Desconhecido"}
                                </Text>
                                <Text size="sm">
                                  <strong>Notas Tomados:</strong>{" "}
                                  {lastDownloadJob.notas_tomados ?? 0}
                                  {lastDownloadJob.portal_total_tomados != null
                                    ? ` (portal: ${lastDownloadJob.portal_total_tomados})`
                                    : ""}
                                </Text>
                                <Text size="sm">
                                  <strong>Notas Prestados:</strong>{" "}
                                  {lastDownloadJob.notas_prestados ?? 0}
                                  {lastDownloadJob.portal_total_prestados != null
                                    ? ` (portal: ${lastDownloadJob.portal_total_prestados})`
                                    : ""}
                                </Text>
                                <Text size="sm">
                                  <strong>Total:</strong>{" "}
                                  {lastDownloadJob.notas_total ?? 0}
                                </Text>
                                <Text size="sm">
                                  <strong>Período:</strong>{" "}
                                  {`${formatDate(lastDownloadJob.start_date)} a ${formatDate(
                                    lastDownloadJob.end_date,
                                  )}`}
                                </Text>
                              </Stack>
                            ) : (
                              <Text type="supporting">
                                Nenhum processamento encontrado.
                              </Text>
                            )}
                          </Stack>
                        </Card>

                        <TextInput
                          label="Código da Empresa"
                          placeholder="Ex: EMP001"
                          value={formData.code}
                          onChange={setFormField("code")}
                        />
                        <TextInput
                          label="Nome da Empresa"
                          isRequired
                          value={formData.name}
                          onChange={setFormField("name")}
                        />
                        <TextInput
                          label="CNPJ"
                          isRequired
                          value={formData.cnpj}
                          onChange={setFormField("cnpj")}
                        />
                        <TextInput
                          label="Login do Portal Nacional"
                          isOptional
                          placeholder="Login de acesso ao portal"
                          value={formData.portal_login}
                          onChange={setFormField("portal_login")}
                        />
                        <TextInput
                          label="Nova Senha do Portal"
                          isOptional
                          type="password"
                          htmlName="portal_password"
                          description="Deixe em branco para manter a senha atual."
                          placeholder="Deixe em branco para manter a atual"
                          value={formData.portal_password}
                          onChange={setFormField("portal_password")}
                        />

                        {/* Certificado digital */}
                        <Card padding={3}>
                          <Stack gap={2}>
                            <HStack gap={2} vAlign="center" hAlign="between">
                              <Text weight="semibold">Certificado digital</Text>
                              <HStack gap={1} vAlign="center">
                                {editingCompany?.cert_status !== "none" && (
                                  <Button
                                    label={
                                      certRemoving
                                        ? "A remover…"
                                        : "Remover certificado"
                                    }
                                    variant="destructive"
                                    size="sm"
                                    isDisabled={certRemoving}
                                    onClick={handleRemoveCertificate}
                                  />
                                )}
                                <IconButton
                                  label={
                                    certUploadExpanded
                                      ? "Ocultar substituição de certificado"
                                      : "Substituir certificado (.pfx)"
                                  }
                                  tooltip={
                                    certUploadExpanded
                                      ? "Ocultar substituição de certificado"
                                      : "Substituir certificado (.pfx)"
                                  }
                                  icon={
                                    <FontAwesomeIcon
                                      icon={certUploadExpanded ? "minus" : "plus"}
                                    />
                                  }
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    setCertUploadExpanded((prev) => !prev)
                                  }
                                />
                              </HStack>
                            </HStack>
                            {editingCompany?.cert_status === "none" ? (
                              <Text type="supporting">
                                Nenhum certificado digital cadastrado. Use “+” para
                                enviar um ficheiro .pfx ou mantenha em branco para não
                                alterar.
                              </Text>
                            ) : (
                              <Stack gap={0.5}>
                                <Text size="sm">
                                  <strong>Validade:</strong>{" "}
                                  {editingCompany.cert_expires_at
                                    ? new Date(
                                        editingCompany.cert_expires_at,
                                      ).toLocaleDateString("pt-BR")
                                    : "—"}
                                </Text>
                                <Text size="sm">
                                  <strong>Estado:</strong>{" "}
                                  {editingCompany.cert_status === "valid"
                                    ? "Válido"
                                    : editingCompany.cert_status === "expiring"
                                      ? "A vencer"
                                      : editingCompany.cert_status === "expired"
                                        ? "Vencido"
                                        : editingCompany.cert_status || "—"}
                                </Text>
                              </Stack>
                            )}
                            {certUploadExpanded && (
                              <Stack gap={2}>
                                <Text type="supporting">
                                  Selecione um novo .pfx e a respectiva senha para
                                  substituir o certificado ao guardar.
                                </Text>
                                <FileInput
                                  label="Ficheiro .pfx"
                                  accept=".pfx,application/x-pkcs12"
                                  value={certPfxFile}
                                  onChange={setCertPfxFile}
                                />
                                <TextInput
                                  label="Senha do Certificado"
                                  type="password"
                                  htmlName="cert_pfx_password"
                                  placeholder="Obrigatória ao anexar .pfx"
                                  value={certPfxPassword}
                                  onChange={setCertPfxPassword}
                                />
                              </Stack>
                            )}
                          </Stack>
                        </Card>

                        <HStack gap={3} vAlign="end">
                          <StackItem size="fill">
                            <Selector
                              label="Frequência"
                              options={FREQUENCY_OPTIONS}
                              value={formData.download_frequency}
                              onChange={setFormField("download_frequency")}
                            />
                          </StackItem>
                          <CheckboxInput
                            label="Download Automático"
                            value={formData.auto_download_enabled}
                            onChange={setFormField("auto_download_enabled")}
                          />
                          <CheckboxInput
                            label="Não captura nota"
                            value={formData.nao_captura_nota}
                            onChange={setFormField("nao_captura_nota")}
                          />
                        </HStack>
                        </Stack>
                        )}

                        {editActiveTab === "contatos" && (
                        // Enter dentro da aba de contatos não pode submeter o
                        // form principal do modal (que salva a empresa).
                        <div
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (newContactEmail.trim()) {
                                handleAddContact();
                              }
                            }
                          }}
                        >
                        <Stack gap={3}>
                          <Text type="supporting">
                            Estes contatos recebem o aviso semanal quando a
                            empresa está com certificado vencido ou sem
                            certificado.
                          </Text>
                          {contactsLoading ? (
                            <Text type="supporting">Carregando contatos...</Text>
                          ) : contactsError ? (
                            <Banner status="error" title={contactsError} />
                          ) : contacts.length === 0 ? (
                            <Text type="supporting">
                              Nenhum contato cadastrado. Adicione e-mails para
                              os avisos de certificado.
                            </Text>
                          ) : (
                            <Stack gap={2}>
                              {contacts.map((contact) => (
                                <Card key={contact.id} padding={2}>
                                  <HStack gap={2} vAlign="center">
                                    <StackItem size="fill">
                                      <Stack gap={0.5}>
                                        <Text weight="semibold">
                                          {contact.name || "Sem nome"}
                                        </Text>
                                        <Text size="sm" type="supporting">
                                          {contact.email}
                                        </Text>
                                      </Stack>
                                    </StackItem>
                                    <CheckboxInput
                                      label="Ativo"
                                      value={contact.is_active}
                                      isDisabled={contactBusyId === contact.id}
                                      onChange={() =>
                                        handleToggleContactActive(contact)
                                      }
                                    />
                                    <IconButton
                                      label="Remover contato"
                                      tooltip="Remover contato"
                                      icon={<FontAwesomeIcon icon="trash" />}
                                      variant="ghost"
                                      size="sm"
                                      isDisabled={contactBusyId === contact.id}
                                      onClick={() => handleDeleteContact(contact)}
                                    />
                                  </HStack>
                                </Card>
                              ))}
                            </Stack>
                          )}
                          <Card padding={3}>
                            <Stack gap={2}>
                              <Text weight="semibold">Adicionar contato</Text>
                              <TextInput
                                label="Nome (opcional)"
                                htmlName="new_contact_name"
                                value={newContactName}
                                onChange={setNewContactName}
                              />
                              <TextInput
                                label="E-mail"
                                type="email"
                                htmlName="new_contact_email"
                                placeholder="contato@empresa.com.br"
                                value={newContactEmail}
                                onChange={setNewContactEmail}
                              />
                              <HStack gap={2} hAlign="end">
                                <Button
                                  label={
                                    savingContact ? "Adicionando…" : "Adicionar"
                                  }
                                  variant="secondary"
                                  type="button"
                                  size="sm"
                                  isDisabled={savingContact}
                                  icon={<FontAwesomeIcon icon="plus" />}
                                  onClick={handleAddContact}
                                />
                              </HStack>
                            </Stack>
                          </Card>
                        </Stack>
                        </div>
                        )}
                      </Stack>
                    </LayoutContent>
                  }
                  footer={
                    <LayoutFooter hasDivider>
                      <HStack gap={2} hAlign="end">
                        <Button
                          label="Cancelar"
                          variant="ghost"
                          onClick={closeEditCompanyModal}
                        />
                        {editingCompany.status === "active" ? (
                          <Button
                            label="Inativar Empresa"
                            variant="destructive"
                            onClick={() => inactivateCompany(editingCompany.id)}
                          />
                        ) : (
                          <Button
                            label="Ativar Empresa"
                            variant="secondary"
                            onClick={() => toggleCompanyStatus(editingCompany.id)}
                          />
                        )}
                        <Button
                          label="Salvar"
                          variant="primary"
                          type="submit"
                          icon={<FontAwesomeIcon icon="save" />}
                        />
                      </HStack>
                    </LayoutFooter>
                  }
                />
              </form>
            </Dialog>
          )}

        {/* Modal de Auditoria de Inativação */}
        {isAuditModalOpen && editingCompany && (
          <Dialog
            isOpen
            onOpenChange={(open) => {
              if (!open) closeAuditModal();
            }}
            width={600}
          >
            <Stack gap={3}>
              <DialogHeader
                title="Auditoria"
                onOpenChange={(open) => {
                  if (!open) closeAuditModal();
                }}
                hasDivider
              />
              <Banner
                status="warning"
                title={`Empresa: ${editingCompany.name}`}
                description={`Status atual: ${
                  editingCompany.status === "inactive" ? "Inativa" : "Ativa"
                }`}
              />

              {loadingInactivationLogs ? (
                <Text type="supporting">Carregando histórico...</Text>
              ) : inactivationLogsError ? (
                <Banner status="error" title={inactivationLogsError} />
              ) : inactivationLogs.length === 0 ? (
                <Text type="supporting">
                  Nenhuma alteração de inativação encontrada.
                </Text>
              ) : (
                <Stack gap={2}>
                  {inactivationLogs.map((log) => (
                    <Card key={log.id} padding={3}>
                      <Stack gap={0.5}>
                        <Text size="sm">{`Empresa inativada por ${
                          log.user_name || "Sistema"
                        }`}</Text>
                        <Text type="supporting">
                          {formatDateTime(log.created_at).replace(",", " -")}
                        </Text>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
              <HStack gap={2} hAlign="end">
                <Button
                  label="Fechar"
                  variant="ghost"
                  onClick={closeAuditModal}
                />
              </HStack>
            </Stack>
          </Dialog>
        )}

        {/* Modal de Importação CSV */}
        {isCSVImportOpen && (
          <CSVImport
            onImportComplete={handleCSVImportComplete}
            onClose={closeCSVImportModal}
          />
        )}

        {/* Modal de Download por Período */}
        {isPeriodDownloadModalOpen && (
          <Dialog
            isOpen
            onOpenChange={(open) => {
              if (!open) closePeriodDownloadModal();
            }}
            width={520}
          >
            <Stack gap={3}>
              <DialogHeader
                title="Download por Período"
                onOpenChange={(open) => {
                  if (!open) closePeriodDownloadModal();
                }}
                hasDivider
              />
              <PeriodDownloadForm
                onSubmit={handlePeriodDownload}
                loading={periodDownloadLoading}
                onCancel={closePeriodDownloadModal}
              />
            </Stack>
          </Dialog>
        )}

        {/* Modal de Logs de Download */}
        <DownloadLogsModal
          isOpen={isLogsModalOpen}
          onClose={closeLogsModal}
          company={selectedCompanyForLogs}
          filters={{}}
        />
      </Stack>
    </AstryxTheme>
  );
}

const COMPANY_GROUP_OPTIONS = [
  { value: "assescont", label: "Assescont" },
  { value: "comsind", label: "Comsind" },
  { value: "advisa", label: "Advisa" },
];

// Componente para o formulário de download por período
function PeriodDownloadForm({ onSubmit, loading, onCancel }) {
  const [formData, setFormData] = useState({
    startDate: "2025-09-01",
    endDate: "2025-09-23",
    companyGroup: "assescont",
  });

  const setField = (name) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // DateInput do Astryx não bloqueia submit vazio (só marca aria-required)
    if (!formData.startDate || !formData.endDate) {
      alert("Informe a data de início e a data de fim.");
      return;
    }

    // Convert dates to ISO format without timezone issues
    const startDate = new Date(formData.startDate + "T00:00:00");
    const endDate = new Date(formData.endDate + "T23:59:59");
    const now = new Date();

    // If the selected end date is today, cap it at current time
    // to avoid "future date" validation errors during the same day.
    if (formData.endDate === now.toISOString().split("T")[0] && endDate > now) {
      endDate.setTime(now.getTime());
    }

    // Format as ISO string for backend
    const startDateISO = startDate.toISOString();
    const endDateISO = endDate.toISOString();

    onSubmit(startDateISO, endDateISO, formData.companyGroup);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={3}>
        <HStack gap={2}>
          <StackItem size="fill">
            <DateInput
              label="Data de Início"
              isRequired
              value={formData.startDate}
              onChange={setField("startDate")}
            />
          </StackItem>
          <StackItem size="fill">
            <DateInput
              label="Data de Fim"
              isRequired
              value={formData.endDate}
              onChange={setField("endDate")}
            />
          </StackItem>
        </HStack>
        <Selector
          label="Grupo de Empresas"
          isRequired
          options={COMPANY_GROUP_OPTIONS}
          value={formData.companyGroup}
          onChange={setField("companyGroup")}
        />
        <Banner
          status="info"
          title="Informações sobre o download"
          description={
            <Stack gap={0.5}>
              <Text size="sm">
                • O download será executado apenas nas empresas do grupo
                selecionado.
              </Text>
              <Text size="sm">
                • As notas fiscais serão baixadas do período especificado (data
                de início e data de fim).
              </Text>
              <Text size="sm">
                • O download não pode ultrapassar o período de 30 dias,
                limitação do portal nacional.
              </Text>
              <Text size="sm">
                • O processo pode levar alguns minutos para ser concluído.
              </Text>
              <Text size="sm">
                • Você receberá uma notificação por email quando o download for
                concluído.
              </Text>
            </Stack>
          }
        />
        <HStack gap={2} hAlign="end">
          <Button
            label="Cancelar"
            variant="ghost"
            isDisabled={loading}
            onClick={onCancel}
          />
          <Button
            label={loading ? "Iniciando..." : "Iniciar Download"}
            variant="primary"
            type="submit"
            icon={<FontAwesomeIcon icon="download" />}
            isLoading={loading}
          />
        </HStack>
      </Stack>
    </form>
  );
}
